import { createApi } from '@reduxjs/toolkit/query/react';

import { createApiBaseQuery, Methods } from '@app/api/shared';
import {
  TechnologicalMapsEndpoints as Endpoints,
  type DeleteClientServiceProductUsagesRequest,
  type DeleteClientServiceProductUsagesResponse,
  type DeleteTechnologicalMapRequest,
  type DeleteTechnologicalMapResponse,
  type GetClientServiceProductUsagesRequest,
  type GetClientServiceProductUsagesResponse,
  type GetProductsListRequest,
  type GetProductsListResponse,
  type GetRecordTechnologicalMapRequest,
  type GetRecordTechnologicalMapResponse,
  type GetWarehousesRequest,
  type GetWarehousesResponse,
  type SaveClientServiceProductUsagesRequest,
  type SaveClientServiceProductUsagesResponse,
} from '@app/api/TechnologicalMaps/types';
import { TECHNOLOGICAL_MAPS } from '@app/constants';

export const technologicalMapsApi = createApi({
  reducerPath: 'technologicalMapsApi',
  baseQuery: createApiBaseQuery(TECHNOLOGICAL_MAPS),
  endpoints: ({ mutation, query }) => ({
    [Endpoints.GetClientServiceProductUsages]: query<
      GetClientServiceProductUsagesResponse,
      GetClientServiceProductUsagesRequest
    >({
      query: (params) => ({
        url: Endpoints.GetClientServiceProductUsages,
        method: Methods.GET,
        params,
      }),
    }),
    [Endpoints.SaveClientServiceProductUsages]: mutation<
      SaveClientServiceProductUsagesResponse,
      SaveClientServiceProductUsagesRequest
    >({
      query: ({ data, ...params }) => ({
        url: Endpoints.SaveClientServiceProductUsages,
        method: Methods.POST,
        params,
        data,
      }),
    }),
    [Endpoints.DeleteClientServiceProductUsages]: mutation<
      DeleteClientServiceProductUsagesResponse,
      DeleteClientServiceProductUsagesRequest
    >({
      query: (params) => ({
        url: Endpoints.DeleteClientServiceProductUsages,
        method: Methods.DELETE,
        params,
      }),
    }),
    [Endpoints.GetWarehouses]: query<
      GetWarehousesResponse,
      GetWarehousesRequest
    >({
      query: ({ warehouseIds, ...rest }) => ({
        url: Endpoints.GetWarehouses,
        method: Methods.GET,
        params: {
          ...rest,
          ...(warehouseIds?.length ? { warehouseIds } : {}),
        },
        paramsSerializer: { indexes: null },
      }),
    }),
    [Endpoints.GetProductsList]: query<
      GetProductsListResponse,
      GetProductsListRequest
    >({
      query: ({ productIds, ...rest }) => ({
        url: Endpoints.GetProductsList,
        method: Methods.GET,
        params: {
          ...rest,
          ...(productIds?.length ? { productIds } : {}),
        },
        paramsSerializer: { indexes: null },
      }),
    }),
    [Endpoints.DeleteTechnologicalMap]: mutation<
      DeleteTechnologicalMapResponse,
      DeleteTechnologicalMapRequest
    >({
      query: (params) => ({
        url: Endpoints.DeleteTechnologicalMap,
        method: Methods.DELETE,
        params,
      }),
    }),
    [Endpoints.GetRecordTechnologicalMap]: query<
      GetRecordTechnologicalMapResponse,
      GetRecordTechnologicalMapRequest
    >({
      query: (params) => ({
        url: Endpoints.GetRecordTechnologicalMap,
        method: Methods.GET,
        params,
      }),
    }),
  }),
});

export const {
  useGetClientServiceProductUsagesQuery,
  useLazyGetClientServiceProductUsagesQuery,
  useSaveClientServiceProductUsagesMutation,
  useDeleteClientServiceProductUsagesMutation,
  useGetWarehousesQuery,
  useLazyGetWarehousesQuery,
  useGetProductsListQuery,
  useLazyGetProductsListQuery,
  useDeleteTechnologicalMapMutation,
  useGetRecordTechnologicalMapQuery,
  useLazyGetRecordTechnologicalMapQuery,
} = technologicalMapsApi;
